"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.entryTimeSchema = exports.entryTimePersonSchema = exports.entryTimeTickSchema = exports.entryTimeTickInputSchema = exports.entryTimeTickParamsSchema = exports.ENTRY_TIME_TICK_MAX_SECONDS = void 0;
// Time on the entry (`RPM-106`, `CR-45`, `J-601`).
//
// THE OWNER'S WORDS: "Personal/efficiency tracking but may be billing at another time. It stops
// counting when not on screen. No resetting regardless of where it is initiated from. it keeps adding
// up. ... It should show the total, with a hover to show details from multiple nurse access. Count
// from the browser's own steady clock." `D6` stands: this is the nurse's own time on an entry, kept so
// it can be read later. It is not billing, it counts no monitoring time toward a code, and it names no
// code.
//
// TWO CLOCKS, KEPT APART. The seconds are the browser's (`performance.now()`), counted while the entry
// is open on Enter vitals and the page is visible; nothing on the server adds to them or measures
// them. The server's clock says only WHEN a tick was stored (`stored_at`), which is what each
// person's first and last time are read from. Moving local dev's clock moves those two and never the
// seconds (acceptance row 10).
const zod_1 = require("zod");
/**
 * The most seconds one tick carries: `ett_seconds_bounded` (`rpm_entry_time_ticks`). The time held
 * before the first store is sent whole, so a long first visit is split into ticks of at most this.
 */
exports.ENTRY_TIME_TICK_MAX_SECONDS = 3600;
/** The completion record the tick is counted against, an opaque id (`.claude/rules/phi.md` rule 2). */
exports.entryTimeTickParamsSchema = zod_1.z.strictObject({ alertId: zod_1.z.uuid() });
/**
 * One tick, as the browser sends it every 30 seconds while counting, and when the page hides or the
 * entry closes. The visit id is minted for every opening of the entry and the sequence increases
 * within it, so a tick sent twice — a retry, or a page that sends on hide and again on close — is the
 * same `(visitId, sequence)` and is stored once (acceptance row 6).
 */
exports.entryTimeTickInputSchema = zod_1.z
    .strictObject({
    visitId: zod_1.z.uuid(),
    /** `ett_seq_nonnegative`; the bound above it is the column's integer range, far past any visit. */
    sequence: zod_1.z.int().min(0).max(2_147_483_647),
    /** Whole seconds counted since the visit's last tick: `ett_seconds_bounded`. */
    seconds: zod_1.z.int().min(1).max(exports.ENTRY_TIME_TICK_MAX_SECONDS),
})
    // NO COMPONENT ID, as `rpm-note.ts`'s body has none: fastify-type-provider-zod names the input
    // side of `EntryTimeTick` "EntryTimeTickInput" itself, and a body with that id collides with it.
    .meta({ description: "Seconds counted on an entry since the last tick" });
exports.entryTimeTickSchema = zod_1.z
    .object({
    /** `ALREADY_STORED` is a tick sent again: nothing was written, and the caller has nothing to do. */
    outcome: zod_1.z.enum(["STORED", "ALREADY_STORED"]),
    /** When the server stored the tick, on its own clock: the first store of it, for a repeat. */
    storedAt: zod_1.z.iso.datetime({ offset: true }),
})
    .meta({ id: "EntryTimeTick", description: "One tick of time on an entry, as stored" });
/** One person's time on the entry: what the hover shows for each nurse (acceptance row 4). */
exports.entryTimePersonSchema = zod_1.z.object({
    userId: zod_1.z.string(),
    /** The account's display name; null where the account carries none. */
    name: zod_1.z.string().nullable(),
    totalSeconds: zod_1.z.int().nonnegative(),
    /** How many openings of the entry stored time: distinct visit ids. */
    visits: zod_1.z.int().nonnegative(),
    /** The server's clock when this person's first and last tick were stored. */
    firstAt: zod_1.z.iso.datetime({ offset: true }),
    lastAt: zod_1.z.iso.datetime({ offset: true }),
});
/**
 * The entry's time: the sum over every completion record in its chain (`superseded_by_alert_id`),
 * so a correction's new record continues it (acceptance row 9), and every person who counted any.
 */
exports.entryTimeSchema = zod_1.z
    .object({
    totalSeconds: zod_1.z.int().nonnegative(),
    /** Most time first; the same person never appears twice. */
    people: zod_1.z.array(exports.entryTimePersonSchema),
})
    .meta({ id: "EntryTime", description: "Time on an entry, in total and per person" });
//# sourceMappingURL=rpm-entry-time.js.map